import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import TextFieldWithCheck from '@/components/form/TextFieldWithCheck.tsx';
import { useLookupCadastre } from '@/lib/api/cadastre.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { CadastrePropertySummary, CadastreRawData } from '@inversor-hub/types';

interface CadastreFormFieldProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDataFilled?: (data: CadastrePropertySummary) => void;
}

const CadastreFormField = ({ value, onChange, onDataFilled }: CadastreFormFieldProps) => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const {
    mutate: fetchCadastre,
    data: cadastralData,
    reset: resetCadastre,
    isPending: isCadastralLoading,
  } = useLookupCadastre();
  const { show: showSnackbar } = useSnackbar();

  const handleFetchCadastreData = () => {
    const reference = value?.trim();

    if (!reference) {
      showSnackbar({
        title: 'Referencia catastral requerida',
        message: 'Introduce una referencia catastral antes de consultar.',
        variant: 'error',
      });
      return;
    }

    fetchCadastre(reference, {
      onSuccess: () => {
        setIsConfirmDialogOpen(true);
      },
    });
  };

  const handleConfirmFillData = () => {
    if (!cadastralData) return;

    onDataFilled?.(cadastralData);
    setIsConfirmDialogOpen(false);
    resetCadastre();

    showSnackbar({
      title: 'Datos del catastro aplicados',
      message: 'Los campos se han rellenado con la información del catastro.',
      variant: 'success',
    });
  };

  const handleCancelFillData = () => {
    setIsConfirmDialogOpen(false);
    resetCadastre();
  };

  const renderSummaryRow = (label: string, value?: string | number | null) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    return (
      <Stack key={label} direction="row" spacing={1} justifyContent="space-between">
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500} textAlign="right">
          {value}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <div style={{ marginBottom: '0px' }}>
        <TextFieldWithCheck
          id="cadastralReference"
          name="cadastralReference"
          label="Referencia Catastral"
          variant="outlined"
          value={value}
          onChange={onChange}
          endAdornment={
            <Tooltip title="Obtener datos del Catastro y rellenar campos">
              <span>
                <IconButton
                  size="small"
                  onClick={handleFetchCadastreData}
                  disabled={isCadastralLoading}
                  aria-label="Obtener datos del Catastro"
                >
                  {isCadastralLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <TravelExploreIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          }
        />
        <Typography variant="body1" style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
          Puedes obtener automáticamente los datos del Catastro haciendo clic en el icono
        </Typography>
      </div>

      <Dialog open={isConfirmDialogOpen} onClose={handleCancelFillData} maxWidth="sm" fullWidth>
        <DialogTitle>Rellenar datos del Catastro</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Los siguientes datos se introducirán y sobrescribirán los campos existentes:
          </Typography>
          <Stack spacing={1}>
            {(() => {
              if (!cadastralData) return null;

              const rawData: CadastreRawData = cadastralData.raw || {};

              const updates: Record<string, string> = {};

              let fullAddress = '';
              if (rawData.address) {
                fullAddress = String(rawData.address);
              } else {
                const addressParts: string[] = [];
                if (rawData.streetType && rawData.streetName) {
                  addressParts.push(`${rawData.streetType} ${rawData.streetName}`);
                } else if (rawData.streetName) {
                  addressParts.push(String(rawData.streetName));
                }
                if (rawData.number) addressParts.push(`Nº ${rawData.number}`);
                if (rawData.block) addressParts.push(`Bloque ${rawData.block}`);
                if (rawData.staircase) addressParts.push(`Esc ${rawData.staircase}`);
                if (rawData.floor) addressParts.push(`Planta ${rawData.floor}`);
                if (rawData.door) addressParts.push(`Puerta ${rawData.door}`);
                fullAddress = addressParts.join(', ');
              }

              if (fullAddress) updates.address = fullAddress;
              if (rawData.postalCode || cadastralData.postalCode) {
                updates.postalCode = String(rawData.postalCode || cadastralData.postalCode);
              }
              if (rawData.municipality || cadastralData.municipality) {
                updates.city = String(rawData.municipality || cadastralData.municipality);
              }
              if (rawData.province || cadastralData.province) {
                updates.province = String(rawData.province || cadastralData.province);
              }
              if (rawData.builtSurface || cadastralData.builtSurface) {
                const surface = Number(rawData.builtSurface || cadastralData.builtSurface);
                if (!isNaN(surface)) updates.squareMeters = `${surface} m²`;
              }
              if (rawData.constructionYear || cadastralData.constructionYear) {
                const year = Number(rawData.constructionYear || cadastralData.constructionYear);
                if (!isNaN(year)) updates.buildingYear = String(year);
              }
              if (rawData.floor) {
                updates.buildingFloor = String(rawData.floor);
              }

              const labels: Record<string, string> = {
                address: 'Dirección completa',
                postalCode: 'Código postal',
                city: 'Ciudad',
                province: 'Provincia',
                squareMeters: 'Metros cuadrados',
                buildingYear: 'Año de construcción',
                buildingFloor: 'Planta del edificio',
              };

              return (
                <>
                  {Object.entries(updates).map(([key, value]) =>
                    renderSummaryRow(labels[key] || key, value)
                  )}
                </>
              );
            })()}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            ¿Deseas continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelFillData}>Cancelar</Button>
          <Button onClick={handleConfirmFillData} variant="contained" autoFocus>
            Sí, rellenar campos
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CadastreFormField;
